import { useEffect, useMemo, useState } from 'react';
import type { IPortfolioCodeQualityTrend, IPortfolioCodeQualityTrendAxis } from '@skystone/shared';
import { ErrorBanner, Spinner } from '@/components/ui';
import { portfolioCodeQualityApi } from '@/services/portfolio-code-quality-api';
import type { IMetricViewFilters } from '@/hooks/use-metric-view-filters';
import { buildBucketAxisProps, todayIsoUtc } from '@/lib/metric-view/bucket-format';
import { CodeQualityTrendCard } from './CodeQualityTrendCard';

interface TrendColumnSlotProps {
  filters: IMetricViewFilters;
}

interface ICardCopy {
  title: string;
  definition: string;
  /** True for rating axes (78% A); false for coverage (78%). */
  showGradeASuffix: boolean;
}

// Per-axis copy. Italic subtitle is intentionally short — full SonarSource
// definitions live in `metric_definitions` (US7) and surface via the
// MetricInfoPopover, not here.
const AXIS_COPY: Record<IPortfolioCodeQualityTrendAxis['key'], ICardCopy> = {
  coverage: {
    title: 'Overall coverage',
    definition: 'Median across in-scope repos.',
    showGradeASuffix: false,
  },
  reliability: {
    title: 'Reliability',
    definition: '% of in-scope repos at grade A.',
    showGradeASuffix: true,
  },
  maintainability: {
    title: 'Maintainability',
    definition: '% of in-scope repos at grade A.',
    showGradeASuffix: true,
  },
};

const SONARCLOUD_FALLBACK_URL = 'https://sonarcloud.io/';

/**
 * Trend-column slot — up to three sparkline cards (Coverage, Reliability
 * grade-A%, Maintainability grade-A%) stacked vertically. Axes absent from
 * the response are omitted entirely (no placeholder), and the column re-flows
 * around the surviving cards. Per US4 (PRODOPSAI-332).
 */
export function TrendColumnSlot({ filters }: Readonly<TrendColumnSlotProps>) {
  const [data, setData] = useState<IPortfolioCodeQualityTrend | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    portfolioCodeQualityApi
      .getTrend({
        from: filters.from,
        to: filters.to,
        productId: filters.productId || undefined,
        teamId: filters.teamId || undefined,
      })
      .then((response) => {
        if (cancelled) return;
        setData(response);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Failed to load Code Quality trends.');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters.from, filters.to, filters.productId, filters.teamId]);

  // Hook must run on every render — keep before loading/error early returns.
  const bucketAxis = useMemo(
    () => buildBucketAxisProps({
      today: todayIsoUtc(),
      rangeFrom: filters.from,
      rangeTo: filters.to,
    }),
    [filters.from, filters.to],
  );

  if (loading) {
    return (
      <div
        className="flex items-center justify-center py-12"
        data-testid="code-quality-trend-column"
      >
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="code-quality-trend-column">
        <ErrorBanner message={error} />
      </div>
    );
  }

  const axes = data?.axes ?? [];

  // Centre vertically when fewer than 3 axes survive — no left-aligned gap.
  const containerClass =
    axes.length < 3
      ? 'flex h-full flex-col justify-center space-y-3'
      : 'space-y-3';

  return (
    <div className={containerClass} data-testid="code-quality-trend-column">
      {axes.map((qualityAxis) => {
        const copy = AXIS_COPY[qualityAxis.key];
        return (
          <CodeQualityTrendCard
            key={qualityAxis.key}
            title={copy.title}
            definition={copy.definition}
            metricKey={qualityAxis.metricKey}
            showGradeASuffix={copy.showGradeASuffix}
            buckets={qualityAxis.buckets}
            aggregate={qualityAxis.aggregate}
            priorAggregate={qualityAxis.priorAggregate}
            level={qualityAxis.level}
            sonarCloudUrl={SONARCLOUD_FALLBACK_URL}
            axis={bucketAxis}
          />
        );
      })}
    </div>
  );
}
